import Vue from 'vue';
import PatientView from './PatientView.vue';
import TherapistView from './TherapistView.vue';
import Menu from './Menu.vue';
import AuthView from './AuthView.vue';
import DashboardView from './DashboardView.vue';
import GuideView from './GuideView.vue';
import GuidesIndexView from './GuidesIndexView.vue';
import ContentPageView from './ContentPageView.vue';
import NotFoundView from './NotFoundView.vue';
import ConsentBanner from './components/ConsentBanner.vue';
import VueRouter from 'vue-router';
import { i18nPlugin, setLocale, getStoredLocale, detectBrowserLocale } from './i18n';
import { LOCALES, DEFAULT_LOCALE, localePath, splitLocalePath } from './i18n/locales';
import { applyConsent } from './consent';

Vue.config.productionTip = false;
Vue.use(VueRouter);
Vue.use(i18nPlugin);

// Every page exists once per language, with a /<code> prefix for all but the
// default language (/about, /pl/about; mirrors expatpsychologie.nl), so each
// page has one stable, crawlable URL per locale. Every page also needs an
// entry in ROUTE_META_KEYS in backend/app.py, or the server answers it with a 404.
const pages = [
  { path: '/', component: Menu, name: 'home' },
  { path: '/patient', component: PatientView, name: 'patient' },
  { path: '/controller', component: TherapistView, name: 'controller' },
  { path: '/login', component: AuthView, name: 'login' },
  { path: '/dashboard', component: DashboardView, name: 'dashboard' },
  { path: '/privacy', component: ContentPageView, name: 'privacy', meta: { page: 'privacy' } },
  { path: '/about', component: ContentPageView, name: 'about', meta: { page: 'about' } },
  { path: '/contact', component: ContentPageView, name: 'contact', meta: { page: 'contact' } },
  { path: '/guides', component: GuidesIndexView, name: 'guides' },
  { path: '/guides/:slug', component: GuideView, name: 'guide' }
];

const routes = [];
pages.forEach(page => {
  LOCALES.forEach(code => {
    routes.push({ ...page, path: localePath(page.path, code), name: `${page.name}-${code}` });
  });
});
// Unknown pages: inside a language prefix first, the default language last
LOCALES.filter(code => code !== DEFAULT_LOCALE).forEach(code => {
  routes.push({ path: `/${code}/*`, component: NotFoundView, name: `not-found-${code}` });
});
routes.push({ path: '*', component: NotFoundView, name: `not-found-${DEFAULT_LOCALE}` });

const router = new VueRouter({
  mode: 'history',
  routes
});

// Runs once per page load. If the visitor lands on a locale-less URL (no
// explicit /pl, /nl, ... prefix) and has no saved preference, we redirect
// client-side based on browser language. This never touches URLs that already
// carry an explicit locale, so Googlebot (default en-US, no stored preference)
// always sees stable, deterministic content per URL with no redirect loops.
let localeAutoDetectDone = false;

router.beforeEach((to, from, next) => {
  const { locale } = splitLocalePath(to.path);

  if (!localeAutoDetectDone) {
    localeAutoDetectDone = true;
    if (locale === DEFAULT_LOCALE) {
      const preferred = getStoredLocale() || detectBrowserLocale();
      if (preferred !== DEFAULT_LOCALE) {
        // Keep the query (e.g. ?session=...) and hash, or therapist and
        // patient end up in different sessions
        next({ path: localePath(to.path, preferred), query: to.query, hash: to.hash });
        return;
      }
    }
  }

  setLocale(locale);
  next();
});

router.afterEach((to) => {
  applyConsent(to.path);
});

new Vue({
  router,
  render: h => h('div', [h('router-view'), h(ConsentBanner)]),
}).$mount('#app');
